// Erase to History Tool — erases pixels back to a previous history state.
// Photoshop equivalent: Eraser tool with "Erase to History" mode checked.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { mergeBBox, type BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class EraseToHistoryTool implements Tool {
  readonly id = "erase-to-history";
  readonly shortcut = "E";

  size = 30;
  hardness = 0.5;
  opacity = 1;

  /** The source pixel buffer from the history state to restore to. */
  sourcePixels: Uint8ClampedArray | null = null;
  sourceWidth = 0;
  sourceHeight = 0;

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    if (!this.sourcePixels) return;

    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
    };

    this.stamp(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    const step = Math.max(1, this.size / 4);
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.stamp(ctx, cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private stamp(ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active || !this.sourcePixels) return;
    const { layer, layerSnapshot } = this.active;
    const data = layer.pixels.data;
    const srcData = this.sourcePixels;
    const sw = this.sourceWidth;
    const sh = this.sourceHeight;
    const dw = layer.pixels.width;
    const dh = layer.pixels.height;
    const radius = this.size / 2;
    const hardness = this.hardness;
    const opacity = this.opacity;
    const r2 = radius * radius;

    // Selection mask
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    const selMask = sel?.mask;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(dw - 1, Math.ceil(docX + radius));
    const maxY = Math.min(dh - 1, Math.ceil(docY + radius));

    let touchX0 = dw, touchY0 = dh, touchX1 = -1, touchY1 = -1;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;

        // Selection masking
        if (selMask && selMask[py * dw + px] === 0) continue;

        // Map to source position (centered on doc)
        const sx = Math.round(px + (sw - dw) / 2);
        const sy = Math.round(py + (sh - dh) / 2);
        if (sx < 0 || sx >= sw || sy < 0 || sy >= sh) continue;

        const dstIdx = (py * dw + px) * 4;
        const srcIdx = (sy * sw + sx) * 4;

        // Soft-edge falloff
        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const brushAlpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;
        const blend = brushAlpha * opacity;

        // Blend current pixel toward source (history state)
        data[dstIdx] = Math.round(data[dstIdx]! * (1 - blend) + srcData[srcIdx]! * blend);
        data[dstIdx + 1] = Math.round(data[dstIdx + 1]! * (1 - blend) + srcData[srcIdx + 1]! * blend);
        data[dstIdx + 2] = Math.round(data[dstIdx + 2]! * (1 - blend) + srcData[srcIdx + 2]! * blend);
        data[dstIdx + 3] = Math.round(data[dstIdx + 3]! * (1 - blend) + srcData[srcIdx + 3]! * blend);

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
